// src/components/ListsView.ts
// Shopping lists overview with tabs (Active vs Archived) and creation form

import type { AppState, ShoppingList } from '../types';
import { api } from '../services/api';
import { showToast } from './Toast';

export class ListsView {
  private el: HTMLElement;

  constructor(
    private state: AppState,
    private callbacks: {
      onSelectList: (list: ShoppingList) => void;
      onListCreated: (list: ShoppingList) => void;
      onListDeleted: (listId: string) => void;
      onListArchived: () => void;
      onTabChange: (tab: 'active' | 'archived') => void;
    }
  ) {
    this.el = document.createElement('div');
    this.el.className = 'main-content';
    this.render();
  }

  public getElement(): HTMLElement {
    return this.el;
  }

  public update(state: AppState) {
    this.state = state;
    this.render();
  }

  private render() {
    const { lists, currentTab, currentHousehold } = this.state;
    const isEditor = currentHousehold?.role === 'owner' || currentHousehold?.role === 'editor';

    const activeLists = lists.filter((l) => !l.archived);
    const archivedLists = lists.filter((l) => l.archived);
    const displayedLists = currentTab === 'active' ? activeLists : archivedLists;

    const emptyIcon = currentTab === 'active' ? '🛒' : '📦';
    const emptyTitle = currentTab === 'active' ? 'No active lists' : 'No archived lists';
    const emptyDesc = currentTab === 'active'
      ? (isEditor ? 'Create your first shopping list to get started.' : 'No active shopping lists for this household.')
      : 'Completed and archived lists will appear here.';

    const listsGridHtml = displayedLists.length
      ? displayedLists
          .map((list) => {
            const total = list.totalItems || (list.items ? list.items.length : 0);
            const done = list.checkedItems || (list.items ? list.items.filter((i) => i.checked).length : 0);
            const skipped = list.skippedItems || (list.items ? list.items.filter((i) => i.skipped).length : 0);
            const pct = total > 0 ? Math.round((done / total) * 100) : 0;
            const dateStr = new Date(list.createdAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });

            let badgeHtml = '';
            if (list.shopDone && skipped > 0) {
              badgeHtml = `<span style="color:var(--amber);font-weight:600;">⚠️ ${skipped} not collected</span>`;
            } else if (list.shopDone || (pct === 100 && total > 0)) {
              badgeHtml = `<span style="color:var(--sage);font-weight:600;">✓ Complete</span>`;
            } else if (list.readyToShop) {
              badgeHtml = `<span style="color:var(--sage);font-weight:700;background:var(--sage-light);padding:0.15rem 0.45rem;border-radius:var(--radius-full);border:1px solid var(--sage-border);font-size:0.75rem;">🛒 Ready to Shop</span>`;
            }

            const actionsHtml = isEditor
              ? `
                <div class="list-card-actions" onclick="event.stopPropagation();">
                  ${
                    !list.archived
                      ? `<button class="btn btn-sage archive-list-btn" data-id="${list.id}" style="padding:0.25rem 0.65rem;font-size:0.75rem;">Archive</button>`
                      : `<button class="btn btn-secondary restore-list-btn" data-id="${list.id}" style="padding:0.25rem 0.65rem;font-size:0.75rem;">Restore</button>`
                  }
                  <button class="btn btn-danger delete-list-btn" data-id="${list.id}" style="padding:0.25rem 0.65rem;font-size:0.75rem;">Delete</button>
                </div>
              `
              : '';

            return `
              <div class="list-card ${list.archived ? 'archived' : ''}" data-id="${list.id}">
                <div class="list-card-top">
                  <div class="list-card-title">${escapeHtml(list.name)}</div>
                  <span class="list-card-date">${dateStr}</span>
                </div>
                <div class="list-card-meta">
                  <span>${total} item${total !== 1 ? 's' : ''}</span>
                  <span>•</span>
                  <span>${done} done</span>
                  ${badgeHtml ? `<span>•</span> ${badgeHtml}` : ''}
                </div>
                ${
                  total > 0
                    ? `<div class="progress-track"><div class="progress-fill" style="width:${pct}%;"></div></div>`
                    : ''
                }
                ${actionsHtml}
              </div>
            `;
          })
          .join('')
      : `
        <div style="background:var(--bg-card);border:1px dashed var(--border);border-radius:var(--radius-lg);padding:3rem 1.5rem;text-align:center;">
          <div style="font-size:2.5rem;margin-bottom:0.5rem;">${emptyIcon}</div>
          <div style="font-size:1.15rem;font-weight:600;margin-bottom:0.25rem;">${emptyTitle}</div>
          <div style="font-size:0.85rem;color:var(--ink-secondary);">${emptyDesc}</div>
        </div>
      `;

    this.el.innerHTML = `
      <!-- Tab Navigation -->
      <div class="tab-bar">
        <button class="tab-btn ${currentTab === 'active' ? 'active' : ''}" id="tab-active-btn">
          Active Lists (${activeLists.length})
        </button>
        <button class="tab-btn ${currentTab === 'archived' ? 'active' : ''}" id="tab-archived-btn">
          Archive (${archivedLists.length})
        </button>
      </div>

      <!-- New List Inline Form (Hidden by default) -->
      <div id="new-list-form-container" style="display:none;background:var(--bg-card);border:1px solid var(--border);border-radius:var(--radius-md);padding:1rem;margin-bottom:1.25rem;box-shadow:var(--shadow-sm);">
        <div style="font-size:0.95rem;font-weight:600;margin-bottom:0.6rem;">New Shopping List</div>
        <div style="display:flex;gap:0.5rem;">
          <input type="text" class="input-field" id="new-list-name-input" placeholder="e.g. Weekly Shop, Tesco, Weekend Braai…" style="flex:1;" />
          <button class="btn btn-primary" id="submit-new-list-btn">+ Create</button>
          <button class="btn btn-ghost" id="cancel-new-list-btn">Cancel</button>
        </div>
      </div>

      <!-- Section Header -->
      <div class="section-header">
        <h1 class="section-title">
          ${currentTab === 'active' ? 'Shopping Lists' : 'Archived Lists'}
        </h1>
        ${
          isEditor && currentTab === 'active'
            ? `<button class="btn btn-primary" id="open-new-list-btn">+ New List</button>`
            : ''
        }
      </div>

      <!-- Lists Grid -->
      <div class="list-grid">
        ${listsGridHtml}
      </div>
    `;

    // Tab clicks
    this.el.querySelector('#tab-active-btn')?.addEventListener('click', () => this.callbacks.onTabChange('active'));
    this.el.querySelector('#tab-archived-btn')?.addEventListener('click', () => this.callbacks.onTabChange('archived'));

    // New list toggles
    const newForm = this.el.querySelector('#new-list-form-container') as HTMLElement;
    const nameInput = this.el.querySelector('#new-list-name-input') as HTMLInputElement;

    this.el.querySelector('#open-new-list-btn')?.addEventListener('click', () => {
      if (newForm) {
        newForm.style.display = 'block';
        nameInput?.focus();
      }
    });

    this.el.querySelector('#cancel-new-list-btn')?.addEventListener('click', () => {
      if (newForm) newForm.style.display = 'none';
    });

    const createListAction = async () => {
      const name = nameInput?.value.trim();
      if (!name || !currentHousehold) return;
      try {
        const res = await api.createList(currentHousehold.id, name);
        showToast(`Created list "${name}"`);
        this.callbacks.onListCreated(res.list);
      } catch (err: any) {
        showToast('Error creating list: ' + err.message);
      }
    };

    this.el.querySelector('#submit-new-list-btn')?.addEventListener('click', createListAction);
    nameInput?.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') createListAction();
    });

    // List card clicks
    this.el.querySelectorAll('.list-card').forEach((card) => {
      card.addEventListener('click', () => {
        const id = card.getAttribute('data-id')!;
        const list = this.state.lists.find((l) => l.id === id);
        if (list) this.callbacks.onSelectList(list);
      });
    });

    // Archive / Restore / Delete actions
    this.el.querySelectorAll('.archive-list-btn').forEach((btn) => {
      btn.addEventListener('click', async (e) => {
        e.stopPropagation();
        const id = btn.getAttribute('data-id')!;
        try {
          await api.updateList(id, { archived: true });
          showToast('List archived');
          this.callbacks.onListArchived();
        } catch (err: any) {
          showToast('Error: ' + err.message);
        }
      });
    });

    this.el.querySelectorAll('.restore-list-btn').forEach((btn) => {
      btn.addEventListener('click', async (e) => {
        e.stopPropagation();
        const id = btn.getAttribute('data-id')!;
        try {
          await api.updateList(id, { archived: false });
          showToast('List restored to active');
          this.callbacks.onListArchived();
        } catch (err: any) {
          showToast('Error: ' + err.message);
        }
      });
    });

    this.el.querySelectorAll('.delete-list-btn').forEach((btn) => {
      btn.addEventListener('click', async (e) => {
        e.stopPropagation();
        const id = btn.getAttribute('data-id')!;
        if (confirm('Permanently delete this list and all its items?')) {
          try {
            await api.deleteList(id);
            showToast('List deleted');
            this.callbacks.onListDeleted(id);
          } catch (err: any) {
            showToast('Error: ' + err.message);
          }
        }
      });
    });
  }
}

function escapeHtml(s: string): string {
  return String(s || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}
